// frontend/src/components/AdminTeamDetail/CropModal.js
import React, { useState, useRef, useCallback, useEffect } from 'react';

const toBlob = (canvas, quality) => new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', quality));

const compressImage = async (canvas, targetSizeKB = 3000) => {
    let quality = 0.95;
    let blob = await toBlob(canvas, quality);

    // Iteratively reduce quality until under target size
    while (blob.size > targetSizeKB * 1024 && quality > 0.1) {
        quality -= 0.05;
        blob = await toBlob(canvas, quality);
    }

    return blob;
};

const CropModal = ({ show, imageUrl, playerName, onConfirm, onCancel, title, subtitle, buttonText }) => {
    // Use provided text or defaults
    const modalTitle = title || 'Crop Photo for Sportradar';
    const modalSubtitle = subtitle || `${playerName} - Drag to position, resize corners`;
    const confirmButtonText = buttonText || 'Crop & Sync';
    const canvasRef = useRef(null);
    const imageRef = useRef(null);
    const containerRef = useRef(null);

    const [imageLoaded, setImageLoaded] = useState(false);
    const [cropArea, setCropArea] = useState({ x: 0, y: 0, size: 200 });
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
    const [displayScale, setDisplayScale] = useState(1);
    const [isProcessing, setIsProcessing] = useState(false);

    // Reset state when modal opens with new image
    useEffect(() => {
        if (show && imageUrl) {
            setImageLoaded(false);
            setCropArea({ x: 0, y: 0, size: 200 });
        }
    }, [show, imageUrl]);

    // Handle image load
    const handleImageLoad = useCallback((e) => {
        const img = e.target;
        const naturalWidth = img.naturalWidth;
        const naturalHeight = img.naturalHeight;

        // Calculate display scale (fit image in container max 600px)
        const maxDisplaySize = 600;
        const scale = Math.min(maxDisplaySize / naturalWidth, maxDisplaySize / naturalHeight, 1);
        setDisplayScale(scale);

        setImageSize({ width: naturalWidth, height: naturalHeight });

        // Center crop area with initial size
        const minDim = Math.min(naturalWidth, naturalHeight);
        const initialSize = Math.min(minDim * 0.8, 400);
        setCropArea({
            x: (naturalWidth - initialSize) / 2,
            y: (naturalHeight - initialSize) / 2,
            size: initialSize
        });

        setImageLoaded(true);
    }, []);

    // Mouse handlers for dragging crop area
    const handleMouseDown = (e) => {
        if (e.target.classList.contains('resize-handle')) {
            setIsResizing(true);
        } else {
            setIsDragging(true);
        }
        setDragStart({ x: e.clientX, y: e.clientY });
        e.preventDefault();
    };

    const handleMouseMove = useCallback((e) => {
        if (!isDragging && !isResizing) return;

        const dx = (e.clientX - dragStart.x) / displayScale;
        const dy = (e.clientY - dragStart.y) / displayScale;

        setCropArea(prev => {
            if (isResizing) {
                // Resize: change size based on diagonal movement
                const delta = (dx + dy) / 2;
                const newSize = Math.max(100, Math.min(
                    prev.size + delta,
                    Math.min(imageSize.width - prev.x, imageSize.height - prev.y)
                ));
                return { ...prev, size: newSize };
            } else {
                // Drag: move position
                const newX = Math.max(0, Math.min(prev.x + dx, imageSize.width - prev.size));
                const newY = Math.max(0, Math.min(prev.y + dy, imageSize.height - prev.size));
                return { ...prev, x: newX, y: newY };
            }
        });

        setDragStart({ x: e.clientX, y: e.clientY });
    }, [isDragging, isResizing, dragStart, displayScale, imageSize]);

    const handleMouseUp = () => {
        setIsDragging(false);
        setIsResizing(false);
    };

    // Touch handlers for mobile
    const handleTouchStart = (e) => {
        const touch = e.touches[0];
        handleMouseDown({ clientX: touch.clientX, clientY: touch.clientY, target: e.target, preventDefault: () => { } });
    };

    const handleTouchMove = (e) => {
        const touch = e.touches[0];
        handleMouseMove({ clientX: touch.clientX, clientY: touch.clientY });
    };

    // Process and confirm crop
    const handleConfirm = async () => {
        if (!imageRef.current) return;

        setIsProcessing(true);
        try {
            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');

            // Set canvas to output size (square, max 1024px for Sportradar)
            const outputSize = Math.min(cropArea.size, 1024);
            canvas.width = outputSize;
            canvas.height = outputSize;

            // Draw cropped area to canvas
            ctx.drawImage(
                imageRef.current,
                cropArea.x, cropArea.y, cropArea.size, cropArea.size,  // Source
                0, 0, outputSize, outputSize  // Destination
            );

            // Compress to under 3MB
            const blob = await compressImage(canvas, 3000);

            // Convert to data URL for upload
            const reader = new FileReader();
            reader.onload = () => {
                onConfirm(reader.result, blob);
            };
            reader.readAsDataURL(blob);
        } catch (error) {
            console.error('Crop error:', error);
        } finally {
            setIsProcessing(false);
        }
    };

    if (!show) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-[70] flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[95vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="p-4 border-b flex items-center justify-between">
                    <div>
                        <h3 className="text-lg font-semibold">{modalTitle}</h3>
                        <p className="text-sm text-gray-500">{modalSubtitle}</p>
                    </div>
                    <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Crop Area */}
                <div
                    ref={containerRef}
                    className="flex-1 overflow-auto p-4 bg-gray-900 flex items-center justify-center"
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleMouseUp}
                >
                    <div
                        className="relative inline-block"
                        style={{
                            width: imageSize.width * displayScale,
                            height: imageSize.height * displayScale
                        }}
                    >
                        {/* Image */}
                        <img
                            ref={imageRef}
                            src={imageUrl}
                            alt="Crop preview"
                            onLoad={handleImageLoad}
                            className="block"
                            style={{
                                width: imageSize.width * displayScale,
                                height: imageSize.height * displayScale,
                                opacity: imageLoaded ? 1 : 0
                            }}
                            crossOrigin="anonymous"
                        />

                        {/* Dark overlay outside crop area */}
                        {imageLoaded && (
                            <>
                                <div className="absolute inset-0 bg-black bg-opacity-50 pointer-events-none" />

                                {/* Crop selection box */}
                                <div
                                    className="absolute border-2 border-white cursor-move"
                                    style={{
                                        left: cropArea.x * displayScale,
                                        top: cropArea.y * displayScale,
                                        width: cropArea.size * displayScale,
                                        height: cropArea.size * displayScale,
                                        boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)'
                                    }}
                                    onMouseDown={handleMouseDown}
                                    onTouchStart={handleTouchStart}
                                >
                                    {/* Grid lines */}
                                    <div className="absolute inset-0 pointer-events-none">
                                        <div className="absolute left-1/3 top-0 bottom-0 w-px bg-white/30" />
                                        <div className="absolute left-2/3 top-0 bottom-0 w-px bg-white/30" />
                                        <div className="absolute top-1/3 left-0 right-0 h-px bg-white/30" />
                                        <div className="absolute top-2/3 left-0 right-0 h-px bg-white/30" />
                                    </div>

                                    {/* Resize handle (bottom-right) */}
                                    <div
                                        className="resize-handle absolute -bottom-2 -right-2 w-4 h-4 bg-white border-2 border-gray-800 cursor-se-resize rounded-sm"
                                    />
                                </div>
                            </>
                        )}

                        {/* Loading placeholder */}
                        {!imageLoaded && (
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="p-4 border-t bg-gray-50 flex items-center justify-between">
                    <div className="text-sm text-gray-500">
                        Crop size: {Math.round(cropArea.size)} × {Math.round(cropArea.size)}px
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={onCancel}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleConfirm}
                            disabled={!imageLoaded || isProcessing}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                        >
                            {isProcessing ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    Processing...
                                </>
                            ) : (
                                <>
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    {confirmButtonText}
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* Hidden canvas for processing */}
                <canvas ref={canvasRef} className="hidden" />
            </div>
        </div>
    );
};

export default CropModal;
